"use client";

// AppleMusicEngine — hidden MusicKit JS player.
// Exposes window.__appleMusicEngine with the same control surface as
// window.__ytEngine so PlaybackEngine.js can route apple_music tracks
// through it without caring which backend is playing.
//
// MusicKit JS v3 plays in the background (no visible iframe/element),
// so there's no off-screen container to render — just headless controls.

import { useEffect, useRef } from "react";
import { useAuth } from "../../lib/auth";

const MUSICKIT_SRC = "https://js-cdn.music.apple.com/musickit/v3/musickit.js";

let musicKitPromise = null;

function loadMusicKit() {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  if (window.MusicKit) return Promise.resolve(window.MusicKit);
  if (musicKitPromise) return musicKitPromise;

  musicKitPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${MUSICKIT_SRC}"]`);
    if (existing) {
      document.addEventListener("musickitloaded", () => resolve(window.MusicKit), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = MUSICKIT_SRC;
    script.async = true;
    script.onerror = () => reject(new Error("Failed to load MusicKit JS"));
    document.addEventListener("musickitloaded", () => resolve(window.MusicKit), { once: true });
    document.body.appendChild(script);
  });
  return musicKitPromise;
}

export default function AppleMusicEngine({ connected }) {
  const { accessToken } = useAuth();
  const instanceRef = useRef(null);
  const readyRef = useRef(false);
  const pendingTrackRef = useRef(null);

  useEffect(() => {
    if (!connected || !accessToken) return;
    let cancelled = false;

    (async () => {
      try {
        const MusicKit = await loadMusicKit();

        // Fetch the developer token from our server
        const res = await fetch("/api/apple-music/token", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) throw new Error("token_fetch_failed");
        const { token } = await res.json();

        if (cancelled) return;

        // Configure (idempotent — MusicKit.configure returns existing instance if already set up)
        const instance = await MusicKit.configure({
          developerToken: token,
          app: { name: "Fulkit", build: "1.0.0" },
        });
        if (cancelled) return;

        instanceRef.current = instance;
        readyRef.current = true;
        console.log("[AppleMusicEngine] Player ready");

        // Wire native MusicKit events so playback state propagates to listeners
        // the instant it changes, instead of waiting on the 500ms polling loop.
        const dispatch = (type, detail) => {
          window.dispatchEvent(new CustomEvent("fulkit:apple-music:" + type, { detail }));
        };

        instance.addEventListener("nowPlayingItemDidChange", (event) => {
          const item = event.item || instance.nowPlayingItem;
          dispatch("nowplaying", {
            sourceId: item?.id || null,
            title: item?.attributes?.name || item?.title || null,
            artist: item?.attributes?.artistName || item?.artistName || null,
            album: item?.attributes?.albumName || item?.albumName || null,
            duration_ms: item?.attributes?.durationInMillis || (item?.playbackDuration ? item.playbackDuration * 1000 : 0),
            art: (item?.attributes?.artwork?.url || "").replace("{w}", "600").replace("{h}", "600") || null,
          });
        });

        instance.addEventListener("playbackStateDidChange", (event) => {
          // MusicKit playbackState enum — 2 = playing
          const state = event.state ?? instance.playbackState;
          dispatch("state", { isPlaying: state === 2, state });
        });

        instance.addEventListener("playbackProgressDidChange", (event) => {
          dispatch("progress", {
            progress: event.progress ?? 0,
            currentTime: (instance.currentPlaybackTime || 0) * 1000,
            duration: (instance.currentPlaybackDuration || 0) * 1000,
          });
        });

        instance.addEventListener("mediaItemDidChange", (event) => {
          const item = event.item;
          if (!item) return;
          dispatch("nowplaying", {
            sourceId: item.id,
            title: item.attributes?.name || item.title || null,
            artist: item.attributes?.artistName || item.artistName || null,
            album: item.attributes?.albumName || item.albumName || null,
            duration_ms: item.attributes?.durationInMillis || 0,
          });
        });

        // Play queued track if any arrived before ready
        if (pendingTrackRef.current) {
          await playTrack(pendingTrackRef.current);
          pendingTrackRef.current = null;
        }
      } catch (err) {
        console.error("[AppleMusicEngine] Init failed:", err?.message);
      }
    })();

    async function playTrack(sourceId) {
      const mk = instanceRef.current;
      if (!mk) return;
      try {
        await mk.setQueue({ song: sourceId });
        await mk.play();
      } catch (err) {
        console.error("[AppleMusicEngine] Play failed:", err?.message);
      }
    }

    return () => {
      cancelled = true;
      try {
        instanceRef.current?.stop?.();
      } catch {}
      readyRef.current = false;
    };
  }, [connected, accessToken]);

  // Expose control surface — shape matches window.__ytEngine so callers
  // can treat either backend identically.
  useEffect(() => {
    if (!connected) return;

    window.__appleMusicEngine = {
      play: async (sourceId) => {
        console.log("[AppleMusicEngine] Play requested:", sourceId);
        if (!readyRef.current || !instanceRef.current) {
          pendingTrackRef.current = sourceId;
          return;
        }
        try {
          await instanceRef.current.setQueue({ song: sourceId });
          await instanceRef.current.play();
        } catch (err) {
          console.error("[AppleMusicEngine] Play failed:", err?.message);
        }
      },
      cue: async (sourceId) => {
        if (!readyRef.current || !instanceRef.current) {
          pendingTrackRef.current = sourceId;
          return;
        }
        try {
          await instanceRef.current.setQueue({ song: sourceId });
        } catch (err) {
          console.error("[AppleMusicEngine] Cue failed:", err?.message);
        }
      },
      pause: () => instanceRef.current?.pause?.(),
      resume: () => instanceRef.current?.play?.(),
      seek: (ms) => instanceRef.current?.seekToTime?.(ms / 1000),
      setVolume: (pct) => {
        if (instanceRef.current) {
          instanceRef.current.volume = Math.max(0, Math.min(1, pct / 100));
        }
      },
      isReady: () => readyRef.current,
      getState: () => {
        const mk = instanceRef.current;
        if (!mk) return null;
        // MusicKit playbackState: 0=none, 2=playing, 3=paused, 4=stopped, 5=ended, 6=seeking, 8=waiting, 9=stalled, 10=buffering
        return {
          isPlaying: mk.playbackState === 2,
          currentTime: (mk.currentPlaybackTime || 0) * 1000,
          duration: (mk.currentPlaybackDuration || 0) * 1000,
          volume: Math.round((mk.volume ?? 0.5) * 100),
        };
      },
    };

    return () => {
      delete window.__appleMusicEngine;
    };
  }, [connected]);

  return null;
}
